import React, { useState, useEffect } from "react";

// ─── Eye Icon ────────────────────────────────────────────────────────────────
const EyeIcon = ({ open }) =>
  open ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );

// ─── Login ────────────────────────────────────────────────────────────────────
export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => { setTimeout(() => setMounted(true), 60); }, []);

  const handleLogin = async () => {
    if (!username || !password) { setError("Please fill in all fields."); return; }
    setError(""); setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const data = await response.json();
      if (data.token) {
        localStorage.setItem("token", data.token);
        localStorage.setItem("username", data.username);
        localStorage.setItem("role", data.role);
        if (data.role === "admin" && new URLSearchParams(window.location.search).get("from") !== "adminhanuman") {
          setError("Access denied. Use the correct admin URL.");
          localStorage.removeItem("token");
          localStorage.removeItem("username");
          localStorage.removeItem("role");
          setLoading(false);
          return;
        }
        window.location.href = data.role === "admin" ? "/" : "/client";
      } else {
        setError(data.message || "Invalid credentials. Please try again.");
      }
    } catch { setError("Unable to connect. Please try again."); }
    finally { setLoading(false); }
  };

  const handleKey = (e) => { if (e.key === "Enter") handleLogin(); };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
        body{background:#000;min-height:100vh;font-family:'Outfit',sans-serif;-webkit-font-smoothing:antialiased;overflow:hidden;}
        ::-webkit-scrollbar{width:4px;}::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.07);border-radius:4px;}
        @keyframes spin{to{transform:rotate(360deg);}}

        .lp-root{position:fixed;inset:0;display:flex;align-items:center;justify-content:center;z-index:10;}

        .lp-card{
          width:420px;
          background:rgba(10,10,10,0.9);
          backdrop-filter:blur(24px);
          border:1px solid rgba(148,163,184,0.1);
          border-radius:20px;
          padding:44px 40px 40px;
          position:relative;
          box-shadow:0 0 0 1px rgba(255,255,255,0.03) inset,0 32px 64px rgba(0,0,0,0.8);
          transform:translateY(${mounted ? "0" : "24px"});
          opacity:${mounted ? 1 : 0};
          transition:transform 0.7s cubic-bezier(0.22,1,0.36,1),opacity 0.7s ease,border-color .25s,box-shadow .25s;
        }
        .lp-card:hover{
          border-color:rgba(99,179,237,0.22);
          box-shadow:0 14px 58px rgba(0,0,0,.65),0 0 0 1px rgba(99,179,237,0.10),0 0 28px rgba(56,189,248,0.07),inset 0 1px 0 rgba(255,255,255,.07);
        }

        .lp-top{display:flex;align-items:center;gap:12px;margin-bottom:28px;}
        .lp-mark{width:38px;height:38px;border-radius:10px;background:linear-gradient(135deg,#6366f1,#8b5cf6);display:flex;align-items:center;justify-content:center;flex-shrink:0;box-shadow:0 4px 16px rgba(99,102,241,0.35);}
        .lp-mark svg{display:block;}
        .lp-brand{font-family:'Outfit',sans-serif;font-size:15px;font-weight:600;color:rgba(255,255,255,0.9);letter-spacing:0.01em;}
        .lp-brand span{display:block;font-size:11px;font-weight:400;color:rgba(148,163,184,0.6);letter-spacing:0.04em;margin-top:1px;}

        .lp-divider{height:1px;background:linear-gradient(90deg,transparent,rgba(148,163,184,0.1),transparent);margin-bottom:32px;}

        .lp-heading{font-family:'Outfit',sans-serif;font-size:26px;font-weight:700;color:#f1f5f9;letter-spacing:-0.02em;margin-bottom:6px;}
        .lp-sub{font-size:13.5px;color:rgba(148,163,184,0.55);margin-bottom:32px;line-height:1.5;}

        .lp-field{margin-bottom:18px;}
        .lp-label{display:block;font-size:11.5px;font-weight:500;letter-spacing:0.06em;text-transform:uppercase;color:rgba(148,163,184,0.5);margin-bottom:8px;}

        .lp-input-wrap{position:relative;border-radius:11px;background:rgba(255,255,255,0.04);border:1px solid rgba(148,163,184,0.09);transition:border-color 0.2s,box-shadow 0.2s;}
        .lp-input-wrap.is-focused{border-color:rgba(99,179,237,0.55);box-shadow:0 0 0 3px rgba(99,179,237,0.09);}

        .lp-input{width:100%;background:transparent;border:none;outline:none;padding:13px 16px;font-size:14px;font-family:'Outfit',sans-serif;color:#e2e8f0;caret-color:#63b3ed;}
        .lp-input::placeholder{color:rgba(148,163,184,0.35);}

        .lp-eye{position:absolute;right:14px;top:50%;transform:translateY(-50%);background:none;border:none;cursor:pointer;color:rgba(100,116,139,0.5);padding:2px;display:flex;align-items:center;transition:color 0.15s;}
        .lp-eye:hover{color:rgba(148,163,184,0.8);}

        .lp-error{background:rgba(239,68,68,0.07);border:1px solid rgba(239,68,68,0.18);border-radius:9px;padding:11px 14px;font-size:13px;color:#fca5a5;margin-bottom:20px;display:flex;align-items:center;gap:8px;}

        .lp-btn{
          width:100%;padding:14px;border:none;border-radius:11px;cursor:pointer;
          font-family:'Outfit',sans-serif;font-size:14.5px;font-weight:600;letter-spacing:0.01em;color:#fff;
          background:linear-gradient(135deg,#0ea5e9 0%,#6366f1 100%);
          box-shadow:0 4px 20px rgba(14,165,233,0.2),0 1px 0 rgba(255,255,255,0.08) inset;
          transition:transform 0.22s,box-shadow 0.22s,opacity 0.15s;
          position:relative;overflow:hidden;
        }
        .lp-btn:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 14px 40px rgba(56,189,248,0.45),0 1px 0 rgba(255,255,255,0.10) inset;}
        .lp-btn:active:not(:disabled){transform:translateY(0);}
        .lp-btn:disabled{opacity:0.55;cursor:not-allowed;transform:none;}

        .lp-btn-inner{display:flex;align-items:center;justify-content:center;gap:8px;}
        .lp-spinner{width:16px;height:16px;border:2px solid rgba(255,255,255,0.3);border-top-color:#fff;border-radius:50%;animation:spin 0.7s linear infinite;}

        .lp-footer{margin-top:24px;display:flex;align-items:center;justify-content:center;}
        .lp-footer-text{font-size:12px;color:rgba(100,116,139,0.4);}

        .lp-dots{position:absolute;inset:0;border-radius:20px;overflow:hidden;pointer-events:none;}
        .lp-dots::before{content:'';position:absolute;inset:0;background-image:radial-gradient(rgba(148,163,184,0.03) 1px,transparent 1px);background-size:20px 20px;}
      `}</style>

      <div className="lp-root">
        <div className="lp-card">
          <div className="lp-dots" />

          <div className="lp-top">
            <div className="lp-mark">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
              </svg>
            </div>
            <div className="lp-brand">
              Orion Suite
              <span>Enterprise Platform</span>
            </div>
          </div>

          <div className="lp-divider" />

          <h1 className="lp-heading">Welcome back</h1>
          <p className="lp-sub">Sign in to continue to your workspace</p>

          <div className="lp-field">
            <label className="lp-label">Username</label>
            <div className={`lp-input-wrap${focused === "user" ? " is-focused" : ""}`}>
              <input
                className="lp-input"
                type="text"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onKeyDown={handleKey}
                onFocus={() => setFocused("user")}
                onBlur={() => setFocused(null)}
                autoComplete="username"
              />
            </div>
          </div>

          <div className="lp-field">
            <label className="lp-label">Password</label>
            <div className={`lp-input-wrap${focused === "pass" ? " is-focused" : ""}`}>
              <input
                className="lp-input"
                type={showPass ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={handleKey}
                onFocus={() => setFocused("pass")}
                onBlur={() => setFocused(null)}
                autoComplete="current-password"
                style={{ paddingRight: "44px" }}
              />
              <button
                className="lp-eye"
                type="button"
                onClick={() => setShowPass((v) => !v)}
                tabIndex={-1}
                aria-label={showPass ? "Hide password" : "Show password"}
              >
                <EyeIcon open={showPass} />
              </button>
            </div>
          </div>

          {error && (
            <div className="lp-error">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}>
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              {error}
            </div>
          )}

          <button className="lp-btn" onClick={handleLogin} disabled={loading}>
            <span className="lp-btn-inner">
              {loading && <span className="lp-spinner" />}
              {loading ? "Authenticating…" : "Sign In"}
            </span>
          </button>

          <div className="lp-footer">
            <span className="lp-footer-text">© 2025 Orion Suite</span>
          </div>
        </div>
      </div>
    </>
  );
}